import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { isIPv4 } from 'is-ip';
import { LoadingPopupComponent } from 'src/app/components/ui/loading-popup/loading-popup.component';
import { DeviceService } from 'src/app/services/devices/device.service';
import { ModalService } from 'src/app/services/modal/modal.service';

interface IpConfig {
  dhcpActivated: boolean;
  ip: string;
  mask: string;
  gateway: string;
  dns: string;
}

// v1 ("network" module, cargadores) vs v2 ("wifi" module, ANC+/AstroLum/Datalog+): mismos
// campos IP, nombres y módulo distintos. AstroLum (v2) no expone dns_sta -> hasDns lo detecta.
const FIELD_MAP = {
  v1: { module: 'network', mode: 'wifiDhcpen', ip: 'wifiipstatic', mask: 'wifimask', gw: 'wifigw', dns: 'wifidns' },
  v2: { module: 'wifi', mode: 'ip_sta_mode', ip: 'ip_sta', mask: 'netmask_sta', gw: 'gw_sta', dns: 'dns_sta' },
};

@Component({
    selector: 'app-wifi',
    templateUrl: './wifi.component.html',
    standalone: false
})
export class WifiComponent implements OnInit {

  @Input()
  allSettings = false;

  // Módulos del dispositivo (ver AllSettingsComponent.modules): determina si se usa el
  // módulo "wifi" (v2) o "network" (v1, legacy).
  @Input()
  modules: string[] = [];

  device = '';
  deviceObj: any;

  dhcpActivated = false;
  ip = '';
  mask = '';
  gateway = '';
  dns = '';
  hasDns = true;

  originalData: IpConfig | null = null;
  successMessage = false;
  error = '';
  modalLoad: NgbModalRef;

  isIPv4 = isIPv4;

  constructor(private _aRoute: ActivatedRoute, public _dService: DeviceService, private _modal: ModalService) { }

  private get fields() {
    return this.modules?.includes('wifi') ? FIELD_MAP.v2 : FIELD_MAP.v1;
  }

  ngOnInit(): void {
    this._aRoute.parent?.parent?.params
      .subscribe((params: Params) => this.device = params['serial']);

    this.deviceObj = this._dService.getActiveDeviceInfo();

    this._dService.getModuleInfo(this.device, this.fields.module)
      .subscribe(data => this.applyCfg(data.cfg));
  }

  private applyCfg(cfg: any): void {
    const f = this.fields;
    this.hasDns = cfg?.[f.dns] !== undefined;
    this.dhcpActivated = !!cfg?.[f.mode];
    this.ip = cfg?.[f.ip] || '';
    this.mask = cfg?.[f.mask] || '';
    this.gateway = cfg?.[f.gw] || '';
    this.dns = this.hasDns ? (cfg?.[f.dns] || '') : '';
    this.originalData = {
      dhcpActivated: this.dhcpActivated,
      ip: this.ip,
      mask: this.mask,
      gateway: this.gateway,
      dns: this.dns,
    };
  }

  get changesMade(): boolean {
    if (!this.originalData) return false;
    return this.dhcpActivated !== this.originalData.dhcpActivated
      || this.ip !== this.originalData.ip
      || this.mask !== this.originalData.mask
      || this.gateway !== this.originalData.gateway
      || this.dns !== this.originalData.dns;
  }

  get hasInvalidStaticValues(): boolean {
    if (this.dhcpActivated) return false;
    return !isIPv4(this.ip) || !isIPv4(this.mask) || !isIPv4(this.gateway) || (this.hasDns && !isIPv4(this.dns));
  }

  get saveDisabled(): boolean {
    return !this.changesMade || this.hasInvalidStaticValues;
  }

  save(): void {
    this.modalLoad = this._modal.open(LoadingPopupComponent);

    const f = this.fields;
    const body: any = this.dhcpActivated
      ? { [f.mode]: 1 }
      : {
        [f.mode]: 0,
        [f.ip]: this.ip,
        [f.mask]: this.mask,
        [f.gw]: this.gateway,
        ...(this.hasDns ? { [f.dns]: this.dns } : {}),
      };
    // Si la respuesta no trae `cfg` (backend no lo garantiza en todas las pantallas), se
    // reconstruye el estado esperado completo en vez de usar `body`: en un guardado DHCP
    // `body` solo lleva el modo, y usarlo tal cual borraría ip/mask/gateway/dns y falsearía
    // `hasDns` en el estado local.
    const expectedCfg = {
      [f.mode]: this.dhcpActivated ? 1 : 0,
      [f.ip]: this.ip,
      [f.mask]: this.mask,
      [f.gw]: this.gateway,
      ...(this.hasDns ? { [f.dns]: this.dns } : {}),
    };

    this._dService.setModuleCfg(this.device, body, f.module)
      .subscribe(data => {
        this.modalLoad.close();
        this.applyCfg(data.cfg ?? expectedCfg);
        this.successMessage = true;
        setTimeout(() => this.successMessage = false, 10000);
      }, err => {
        console.error(err);
        this.error = err.error?.message || '';
        setTimeout(() => this.error = '', 10000);
        this.modalLoad.close();
      });
  }

  restore(): void {
    if (!this.originalData) return;
    this.dhcpActivated = this.originalData.dhcpActivated;
    this.ip = this.originalData.ip;
    this.mask = this.originalData.mask;
    this.gateway = this.originalData.gateway;
    this.dns = this.originalData.dns;
  }

}
